// apps/web/src/pages/admin/reservations/ReservationFilters.tsx — Phase 5 plan 05-04 (B4).
//
// Filter sheet (`<dialog>` element) with status + source checkboxes +
// Apply + Clear buttons. The parent owns the filter state; this
// component only reads + bubbles up via `onApply` / `onClear`.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export type ReservationStatusFilter =
  "bestätigt" | "angefragt" | "angekommen" | "no_show" | "storniert";

export type ReservationSourceFilter = "online" | "telefon" | "vor_ort";

export interface ReservationFilterValue {
  status: ReservationStatusFilter[];
  source: ReservationSourceFilter[];
}

export const ALLOWED_STATUS = [
  "bestätigt",
  "angefragt",
  "angekommen",
  "no_show",
  "storniert",
] as const satisfies readonly ReservationStatusFilter[];

export const ALLOWED_SOURCE = [
  "online",
  "telefon",
  "vor_ort",
] as const satisfies readonly ReservationSourceFilter[];

export const EMPTY_FILTER: ReservationFilterValue = {
  status: [],
  source: [],
};

export function ReservationFilters({
  open,
  value,
  onApply,
  onClear,
  onClose,
}: {
  open: boolean;
  value: ReservationFilterValue;
  onApply: (next: ReservationFilterValue) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) dlg.showModal();
    if (!open && dlg.open) dlg.close();
  }, [open]);

  const toggle = <K extends keyof ReservationFilterValue>(
    key: K,
    item: ReservationFilterValue[K][number],
  ) => {
    const current = value[key] as readonly string[];
    const next = current.includes(item)
      ? (current.filter((v) => v !== item) as ReservationFilterValue[K])
      : ([...current, item] as ReservationFilterValue[K]);
    onApply({ ...value, [key]: next });
  };

  const node = (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="card p-6 w-full max-w-md"
      data-testid="reservation-filters-dialog"
    >
      <h2 className="font-heading text-xl mb-4">
        {t("admin.reservations.filters.title")}
      </h2>
      <fieldset className="mb-4">
        <legend className="font-medium mb-2">
          {t("admin.reservations.filters.status")}
        </legend>
        <div className="flex flex-col gap-2">
          {ALLOWED_STATUS.map((s) => (
            <label key={s} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.status.includes(s)}
                onChange={() => toggle("status", s)}
                data-testid={`filter-status-${s}`}
              />
              <span>{t(`status.${s}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="mb-4">
        <legend className="font-medium mb-2">
          {t("admin.reservations.filters.source")}
        </legend>
        <div className="flex flex-col gap-2">
          {ALLOWED_SOURCE.map((s) => (
            <label key={s} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={value.source.includes(s)}
                onChange={() => toggle("source", s)}
                data-testid={`filter-source-${s}`}
              />
              <span>{t(`source.${s}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClear}
          className="btn btn-outline"
          data-testid="filter-clear"
        >
          {t("admin.reservations.filters.clear")}
        </button>
        <button
          type="button"
          onClick={() => {
            onApply(value);
            onClose();
          }}
          className="btn btn-primary"
          data-testid="filter-apply"
        >
          {t("admin.reservations.filters.apply")}
        </button>
      </div>
    </dialog>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}
